import { useState, useEffect } from 'react'
import { Plus, Trash2, Pencil, Eye, EyeOff, X, Save } from 'lucide-react'
import { fetchAdminDocs, createDoc, updateDoc, deleteDoc } from '../../services/admin'
import type { DocArticle } from '../../services/admin'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function DocsPanel() {
  const [docs, setDocs] = useState<DocArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  // Create form state
  const [formTitle, setFormTitle] = useState('')
  const [formSlug, setFormSlug] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formPublished, setFormPublished] = useState(false)
  const [slugManual, setSlugManual] = useState(false)
  const [saving, setSaving] = useState(false)

  // Edit form state
  const [editTitle, setEditTitle] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editPublished, setEditPublished] = useState(false)
  const [editSortOrder, setEditSortOrder] = useState(0)

  useEffect(() => { loadDocs() }, [])

  async function loadDocs() {
    setLoading(true)
    try {
      const data = await fetchAdminDocs()
      setDocs(data)
    } catch {
      // Error handled
    } finally {
      setLoading(false)
    }
  }

  function handleTitleChange(title: string) {
    setFormTitle(title)
    if (!slugManual) setFormSlug(slugify(title))
  }

  async function handleCreate() {
    if (!formTitle.trim() || !formSlug.trim()) return
    setSaving(true)
    try {
      await createDoc({
        title: formTitle.trim(),
        slug: formSlug.trim(),
        content: formContent,
        published: formPublished,
      })
      setShowForm(false)
      setFormTitle('')
      setFormSlug('')
      setFormContent('')
      setFormPublished(false)
      setSlugManual(false)
      await loadDocs()
    } catch {
      // Error handled
    } finally {
      setSaving(false)
    }
  }

  function startEdit(doc: DocArticle) {
    setEditingId(doc.id)
    setEditTitle(doc.title)
    setEditSlug(doc.slug)
    setEditContent(doc.content)
    setEditPublished(doc.published)
    setEditSortOrder(doc.sort_order)
  }

  function cancelEdit() {
    setEditingId(null)
  }

  async function handleUpdate() {
    if (!editingId || !editTitle.trim() || !editSlug.trim()) return
    setSaving(true)
    try {
      await updateDoc(editingId, {
        title: editTitle.trim(),
        slug: editSlug.trim(),
        content: editContent,
        published: editPublished,
        sort_order: editSortOrder,
      })
      setEditingId(null)
      await loadDocs()
    } catch {
      // Error handled
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this doc? This cannot be undone.')) return
    try {
      await deleteDoc(id)
      if (editingId === id) setEditingId(null)
      setDocs(prev => prev.filter(d => d.id !== id))
    } catch {
      // Error handled
    }
  }

  async function togglePublished(doc: DocArticle) {
    try {
      await updateDoc(doc.id, { published: !doc.published })
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, published: !d.published } : d))
    } catch {
      // Error handled
    }
  }

  if (loading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading docs...</p>
  }

  // Edit view
  if (editingId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Edit Doc</h3>
          <button
            onClick={cancelEdit}
            className="p-1.5 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="glass rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Title</label>
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Slug</label>
              <input
                value={editSlug}
                onChange={e => setEditSlug(e.target.value)}
                className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Sort Order</label>
              <input
                type="number"
                value={editSortOrder}
                onChange={e => setEditSortOrder(parseInt(e.target.value) || 0)}
                className="w-24 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPublished}
                  onChange={e => setEditPublished(e.target.checked)}
                  className="accent-cyan-500"
                />
                <span className="font-mono text-xs text-slate-400">Published</span>
              </label>
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Content (Markdown)</label>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={16}
              className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none resize-y leading-relaxed"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleUpdate}
              disabled={saving}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 rounded-lg font-mono text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Create button / form */}
      {showForm ? (
        <div className="glass rounded-xl p-4 space-y-3">
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Create Doc</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Title</label>
              <input
                value={formTitle}
                onChange={e => handleTitleChange(e.target.value)}
                placeholder="Getting Started"
                className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Slug</label>
              <input
                value={formSlug}
                onChange={e => { setFormSlug(e.target.value); setSlugManual(true) }}
                placeholder="getting-started"
                className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Content (Markdown)</label>
            <textarea
              value={formContent}
              onChange={e => setFormContent(e.target.value)}
              rows={8}
              placeholder="Write your documentation here..."
              className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-2 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none resize-y leading-relaxed"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formPublished}
              onChange={e => setFormPublished(e.target.checked)}
              className="accent-cyan-500"
            />
            <span className="font-mono text-xs text-slate-400">Publish immediately</span>
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={saving || !formTitle.trim() || !formSlug.trim()}
              className="px-3 py-1.5 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 rounded-lg font-mono text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Doc
        </button>
      )}

      {/* Docs table */}
      <p className="font-mono text-xs text-slate-500">{docs.length} docs</p>
      {docs.length === 0 ? (
        <p className="font-mono text-xs text-slate-600">No docs yet</p>
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cyan-500/10">
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Title</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Slug</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Order</th>
                <th className="text-center px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Status</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Updated</th>
                <th className="w-24 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.02] transition-colors">
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-200">{doc.title}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{doc.slug}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500 tabular-nums">{doc.sort_order}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => togglePublished(doc)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono transition-colors ${
                        doc.published
                          ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20'
                          : 'bg-slate-500/10 text-slate-500 border border-slate-500/20'
                      }`}
                      title={doc.published ? 'Click to unpublish' : 'Click to publish'}
                    >
                      {doc.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {doc.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">
                    {new Date(doc.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => startEdit(doc)}
                        className="p-1 text-slate-500 hover:text-cyan-400 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
